/**
 * MultiGroupingSelector Component
 *
 * Elegant selector for multi-level grouping options.
 */

import type { ReactNode } from 'react';
import type { GroupingState, GroupByField } from '@/types';
import { InlineSelect } from './InlineSelect';

export interface MultiGroupingSelectorProps {
  grouping: GroupingState;
  onGroupingChange: (grouping: GroupingState) => void;
  availableOptions: Array<{ value: GroupByField; label: string; icon?: ReactNode }>;
  icon: ReactNode;
  compact?: boolean;
}

export function MultiGroupingSelector({
  grouping,
  onGroupingChange,
  availableOptions,
  icon,
  compact = false,
}: MultiGroupingSelectorProps) {
  const generateCombinedOptions = () => {
    const options = [{ value: 'none', label: 'Nessuno' }];

    // Single level grouping options
    availableOptions.forEach((option) => {
      if (option.value !== 'none') {
        options.push({
          value: option.value,
          label: option.label,
        });
      }
    });

    // Two level grouping options
    availableOptions.forEach((primary) => {
      if (primary.value !== 'none') {
        availableOptions.forEach((secondary) => {
          if (secondary.value !== 'none' && secondary.value !== primary.value) {
            options.push({
              value: `${primary.value}-${secondary.value}`,
              label: `${primary.label} → ${secondary.label}`,
            });
          }
        });
      }
    });

    return options;
  };

  const combinedOptions = generateCombinedOptions();

  const getCurrentValue = () => {
    if (grouping.primary === 'none') return 'none';
    if (grouping.secondary === 'none') return grouping.primary;
    return `${grouping.primary}-${grouping.secondary}`;
  };

  const handleGroupingChange = (value: string) => {
    if (value === 'none') {
      onGroupingChange({
        ...grouping,
        primary: 'none',
        secondary: 'none',
      });
    } else if (value.includes('-')) {
      const [primary, secondary] = value.split('-') as [GroupByField, GroupByField];
      onGroupingChange({
        ...grouping,
        primary,
        secondary,
      });
    } else {
      onGroupingChange({
        ...grouping,
        primary: value as GroupByField,
        secondary: 'none',
      });
    }
  };

  return (
    <InlineSelect
      label="Raggruppa"
      icon={icon}
      value={getCurrentValue()}
      onChange={handleGroupingChange}
      options={combinedOptions}
      placeholder="Nessuno"
      searchable={true}
      minWidthClass="min-w-0"
      className="flex-1 min-w-0 w-[120px] sm:w-[150px]"
      compact={compact}
    />
  );
}
